import React, { FC, useContext, useMemo } from 'react';

import { Element, Text } from 'slate';

import styled from 'styled-components';

// theme
import aampeTheme from '../../../../constants/aampeTheme';
import { FormulaType } from '../../../../constants/formulaType';

// context
import { EditorContext } from '../../../../context/EditorContext';

// helpers
import { truncatePreviewHeading } from '../../../../helpers/formulaHelpers';

// models
import {
    ContentCardsPreviewModel,
    ElementKind,
    ImageVariantElement,
} from '../../../../models/editor.model';
import {
    ContentCardType,
    FormulaTypesData,
    PreviewComponentProps,
} from '../../../../models/formula.model';

// components
import Phone from '../shared/Phone';

interface TextContentProps {
    hasImage?: boolean;
    isImageOnly?: boolean;
    isTextOnly?: boolean;
}

const ContentCardsPreview: FC<
    React.PropsWithChildren<PreviewComponentProps<ContentCardsPreviewModel>>
> = ({ randomSample, formulaHasVariant }) => {
    const { editorDetails } = useContext(EditorContext);
    const { formula, formulaType } = editorDetails;

    const contentCardType = useMemo<ContentCardType | undefined>(() => {
        if (formulaType === FormulaType.contentCards) {
            return formula?.content_card_type || ContentCardType.TextOnly;
        }
    }, [formula, formulaType]);

    const hasText = useMemo<boolean>(() => {
        const preview = editorDetails.previews?.[0] as ContentCardsPreviewModel;
        switch (contentCardType) {
            case ContentCardType.ImageOnly:
                return !!preview?.image_url;
            case ContentCardType.ImageAndText:
                return !!(
                    preview?.content.headline ||
                    preview?.content.button_text ||
                    preview?.image_url ||
                    preview?.content.body
                );
            default:
                return !!(
                    preview?.content?.headline ||
                    preview?.content.button_text ||
                    preview?.content.body
                );
        }
    }, [editorDetails, contentCardType]);

    const formulaVariantCheck = useMemo<boolean>(() => {
        if (formulaHasVariant) {
            return formulaHasVariant;
        }
        if (formula && formula?.structure) {
            return !!formula.structure.find((node) => {
                if (
                    Element.isElement(node) &&
                    !Text.isText(node) &&
                    node.kind !== ElementKind.dataset &&
                    node.kind !== ElementKind.button
                ) {
                    if (node.kind === ElementKind.image) {
                        return !!(node as ImageVariantElement)?.id;
                    }
                }
                return false;
            });
        } else {
            return false;
        }
    }, [formula, formulaHasVariant]);

    return (
        <Phone hasText={hasText} formulaHasVariant={formulaVariantCheck}>
            {randomSample.map((msg) =>
                contentCardType === ContentCardType.TextOnly ? (
                    <Message key={msg.id}>
                        <TextContent isTextOnly={true}>
                            <Header>
                                {truncatePreviewHeading(
                                    msg.content.headline,
                                    FormulaTypesData?.[
                                        editorDetails?.formulaType as FormulaType
                                    ].charWarningLimits?.headline as number,
                                )}
                            </Header>
                            <Body>{msg.content.body}</Body>
                        </TextContent>
                        {msg.content?.button_text && (
                            <ButtonContainer>
                                <AnchorTag
                                    href={msg.content?.button_link}
                                    target="_blank"
                                >
                                    {`${msg.content?.button_text} ->`}
                                </AnchorTag>
                            </ButtonContainer>
                        )}
                    </Message>
                ) : contentCardType === ContentCardType.ImageOnly ? (
                    <Message hasImage={true}>
                        {msg?.image_url && (
                            <AnchorTag
                                href={
                                    msg.content?.destination_link_on_image_click
                                }
                                target="_blank"
                            >
                                <ImageContainer
                                    height={'100%'}
                                    src={msg.image_url}
                                    isImageOnly={true}
                                />
                            </AnchorTag>
                        )}
                    </Message>
                ) : (
                    <Message hasImage={true}>
                        {msg?.image_url && (
                            <AnchorTag
                                href={msg.content?.button_link}
                                target="_blank"
                            >
                                <ImageContainer src={msg.image_url} />
                            </AnchorTag>
                        )}
                        <ImageAndTextContainer>
                            <TextContent>
                                <Header>
                                    {truncatePreviewHeading(
                                        msg.content.headline,
                                        FormulaTypesData?.[
                                            editorDetails?.formulaType as FormulaType
                                        ].charWarningLimits?.headline as number,
                                    )}
                                </Header>
                                <Body>{msg.content.body}</Body>
                            </TextContent>
                            {msg.content?.button_text && (
                                <ButtonContainer>
                                    <AnchorTag
                                        href={msg.content?.button_link}
                                        target="_blank"
                                    >
                                        {`${msg.content?.button_text} ->`}
                                    </AnchorTag>
                                </ButtonContainer>
                            )}
                        </ImageAndTextContainer>
                    </Message>
                ),
            )}
        </Phone>
    );
};

export default ContentCardsPreview;

const Message = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'hasImage',
})<TextContentProps>(({ hasImage }) => ({
    backgroundColor: aampeTheme.colors.white,
    padding: hasImage ? 0 : aampeTheme.spacing4(3, 5),
    borderRadius: aampeTheme.borderRadius.default,
    display: 'flex',
    flexFlow: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    width: '82%',
    height: 'fit-content',
    boxShadow: aampeTheme.boxShadow.default,
    whiteSpace: 'pre-wrap',
    color: aampeTheme.colors.neutral09,
    gap: aampeTheme.spacing4(2),
}));

const TextContent = styled.div.withConfig({
    shouldForwardProp: (prop) => prop !== 'isTextOnly',
})<TextContentProps>(({ isTextOnly }) => ({
    display: 'flex',
    flexFlow: 'column nowrap',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    height: 'fit-content',
    fontSize: aampeTheme.size.font.textSmall,
    lineHeight: '20px',
    gap: isTextOnly ? aampeTheme.spacing4(1) : aampeTheme.spacing4(2),
}));
const Header = styled.div({
    fontWeight: 600,
    wordBreak: 'break-word',
    display: 'inline-block',
    lineHeight: '18px',
    fontSize: '14px',
});

const Body = styled.div({
    fontSize: aampeTheme.size.font.textSmall,
    wordBreak: 'break-word',
    display: 'inline-block',
    fontWeight: 400,
    lineHeight: '18px',
});

const ButtonContainer = styled.div({
    display: 'flex',
    fontSize: aampeTheme.size.font.textSmall,
    fontWeight: 400,
    lineHeight: '18px',
    gap: aampeTheme.spacing4(1),
});

const AnchorTag = styled.a({
    textDecoration: 'none',
});

const ImageContainer = styled.img.withConfig({
    shouldForwardProp: (prop) => prop !== 'isImageOnly',
})<TextContentProps>(({ isImageOnly }) => ({
    width: '100%',
    borderBottomLeftRadius: isImageOnly ? aampeTheme.borderRadius.default : 0,
    borderBottomRightRadius: isImageOnly ? aampeTheme.borderRadius.default : 0,
    borderTopLeftRadius: aampeTheme.borderRadius.default,
    borderTopRightRadius: aampeTheme.borderRadius.default,
    overflow: 'hidden',
}));

const ImageAndTextContainer = styled.div({
    margin: aampeTheme.spacing4(2, 4, 2, 4),
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(3),
});
